/**
 * Settings › Needs attention rules, `/settings/needs-attention-rules` (v1
 * completion I §10, the mock-up's state 9): every rule with whether it is on,
 * who it goes to, after how long, when it is due and its priority. Rows are
 * seeded from code; an admin's edit is never overwritten by a deploy. Each row
 * carries Edit rule… when the server presents it (an admin); every staff role
 * reads the table. Paged on the server.
 */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type { ListResponse } from "@shared-core/data/adapter";
import type { AttentionRuleItem } from "@shared-core/data/needs-attention-types";
import { EditRuleDialog } from "../app/components/domain/needs-attention/EditRuleDialog";
import { ErrorPanel } from "../app/components/patterns/ErrorPanel";
import { ListPagination } from "../app/components/patterns/ListPagination";
import { Button } from "../app/components/primitives/button";
import { Skeleton } from "../app/components/primitives/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../app/components/primitives/table";
import { cn } from "../app/components/primitives/utils";

const PAGE_SIZE = 50;
const PRIORITY_WORDS: Record<string, string> = {
  low: "Low",
  normal: "Normal",
  high: "High",
  urgent: "Urgent",
};

export function SettingsNeedsAttentionRulesPage() {
  const adapter = useDataAdapter();
  const [page, setPage] = useState(1);
  const [list, setList] = useState<ListResponse<AttentionRuleItem> | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [reloads, setReloads] = useState(0);
  const reload = useCallback(() => setReloads((n) => n + 1), []);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    adapter
      .listAttentionRules({ page, pageSize: PAGE_SIZE })
      .then((found) => {
        if (!cancelled) setList(found);
      })
      .catch((err: unknown) => {
        if (!cancelled)
          setError(
            err instanceof Error ? err.message : "The rules did not load.",
          );
      });
    return () => {
      cancelled = true;
    };
  }, [adapter, page, reloads]);

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-medium">Needs attention rules</h1>
        <p className="text-sm text-muted-foreground">
          What raises an alert, who it goes to and when it is due. A deploy
          never overwrites an edit.
        </p>
      </div>
      {error ? (
        <ErrorPanel
          message={`The rules did not load: ${error}`}
          onRetry={reload}
        />
      ) : list === null ? (
        <div className="space-y-2" aria-busy="true">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      ) : (
        <div className="rounded-lg border border-border px-3 py-2">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Rule</TableHead>
                <TableHead>On</TableHead>
                <TableHead>Goes to</TableHead>
                <TableHead>After</TableHead>
                <TableHead>Due</TableHead>
                <TableHead>Priority</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {list.items.map((rule) => {
                const edit = rule.commands.find(
                  (c) => c.name === "needs_attention.update_attention_rule",
                );
                return (
                  <TableRow
                    key={rule.code}
                    className={cn(
                      rule.state === "off" && "text-muted-foreground",
                    )}
                  >
                    <TableCell className="whitespace-normal">
                      {rule.label}
                    </TableCell>
                    <TableCell>{rule.state === "on" ? "On" : "Off"}</TableCell>
                    <TableCell className="whitespace-normal">
                      {rule.owner_label}
                    </TableCell>
                    <TableCell>{rule.threshold_label}</TableCell>
                    <TableCell
                      className={
                        rule.due_set_by_time ? "whitespace-normal" : undefined
                      }
                    >
                      {rule.due_label}
                    </TableCell>
                    <TableCell>
                      {PRIORITY_WORDS[rule.priority] ?? rule.priority}
                    </TableCell>
                    <TableCell className="text-right">
                      {edit && (
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={!edit.enabled}
                          aria-label={`Edit rule: ${rule.label}`}
                          onClick={() => setEditing(rule.code)}
                        >
                          {edit.label}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          <ListPagination
            page={list.page}
            totalPages={list.totalPages}
            onPageChange={setPage}
          />
        </div>
      )}
      {editing && (
        <EditRuleDialog
          code={editing}
          open
          onOpenChange={(open) => !open && setEditing(null)}
          onSaved={(summary) => {
            toast.success(summary);
            reload();
          }}
        />
      )}
    </div>
  );
}
